import { supabase } from './supabase';
import { selectDeClientes } from './columnasDeCliente';

// ─────────────────────────────────────────────────────────────────────────────
// AQUÍ SOLO SE LEE.
//
// Este módulo tenía además catorce métodos de escritura —`clientes.update`,
// `cobros.update`, `leads.delete`, `contratos.firmar`, `notificaciones.create`…—
// y **ninguno tenía un solo caller**: medido sobre todo `src/`, de `db` solo se
// usan los `getAll`. Se retiraron por el mismo motivo por el que antes se
// retiró `clientes.delete`: no por estar muertos, sino por lo que decían.
//
// Todos seguían la forma `const { error } = await …; return !error`, que sobre
// un UPDATE o un DELETE es una afirmación falsa: PostgREST responde
// `error: null` cuando la operación no toca ninguna fila, sea porque el id no
// existe o porque RLS la filtró. Ese `true` significaba "el servidor no se
// quejó", y quien lo leyera entendería "se guardó". A un import de distancia de
// cualquier pantalla nueva.
//
// Las escrituras vivas van por `AppContext`, que verifica filas afectadas con
// `updatePorId` / `borrarPorId` (ver `src/lib/updateVerificado.js`), o por la
// Edge correspondiente cuando el hecho necesita autoridad de servidor.
// ─────────────────────────────────────────────────────────────────────────────

export const db = {

  studio: {
    get: async () => {
      const { data } = await supabase.from('studio').select('*').single();
      return data;
    },
  },

  team: {
    getAll: async () => {
      const { data } = await supabase.from('team').select('*').order('created_at');
      return data || [];
    },
  },

  clientes: {
    // ── LA CONSULTA DEPENDE DE QUIEN MIRA ───────────────────────────────────
    //
    // Pedia `select('*')`, y esa fila entera llegaba al navegador del CLIENTE: las
    // 38 columnas de `clientes`, incluidas las notas internas del estudio sobre el,
    // la confianza de atribucion de su adquisicion, su etapa de pipeline y su
    // disenador asignado. RLS no lo impide: gobierna que FILAS se ven, no que
    // columnas.
    //
    // El personal sigue recibiendo lo de siempre, con el `team` embebido que el panel
    // usa para pintar al disenador. Un cliente recibe su minimo. La lista vive en
    // `columnasDeCliente.js` para que la decision este en un solo sitio.
    //
    // @param {boolean} esCliente  `true` cuando quien mira es un cliente del portal.
    getAll: async (esCliente = false) => {
      const { data } = await supabase
        .from('clientes')
        .select(selectDeClientes(esCliente))
        .order('created_at', { ascending: false });
      return data || [];
    },
  },

  proyectos: {
    getAll: async () => {
      const { data } = await supabase
        .from('proyectos')
        .select('*')
        .order('created_at', { ascending: false });
      return data || [];
    },
  },

  cobros: {
    getAll: async () => {
      const { data } = await supabase
        .from('cobros')
        // `clase_de_origen` es columna computada (399): la clasificación la
        // calcula la base siguiendo la causa, y viaja con la fila.
        .select('*, clase_de_origen')
        .order('created_at', { ascending: false });
      return data || [];
    },
  },

  gastos: {
    getAll: async () => {
      const { data } = await supabase
        .from('gastos')
        .select('*')
        .order('created_at');
      return data || [];
    },
  },

  leads: {
    getAll: async () => {
      const { data } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      return data || [];
    },
  },

  notificaciones: {
    getAll: async () => {
      const { data } = await supabase
        .from('notificaciones')
        .select('*')
        .order('created_at', { ascending: false });
      return data || [];
    },
  },

  clientResponsibilities: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('client_responsibilities')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        console.error('[db.clientResponsibilities.getAll] No se pudieron cargar responsabilidades:', error.message || error);
        return [];
      }
      return data || [];
    },
  },

  contratos: {
    getByCliente: async (clienteId) => {
      const { data } = await supabase
        .from('contratos')
        .select('*')
        .eq('cliente_id', clienteId)
        .single();
      return data;
    },
  },
};
